import moment from 'moment';
import {formatDate} from '@pb/kit-formatters';

import {ERROR_TYPES} from '../../typed-input/utils/constants';


type ErrorType = keyof typeof ERROR_TYPES;

const AcceptableDateFormats = [
    ...Object.values({
        DATE_MONTH_YEAR: 'MMM YYYY',
        FULL_DATE_MONTH_YEAR: 'DD MMM YY',
        FULL_MONTH_YEAR: 'MMMM YYYY',
        DATE_MONTH: 'MMM',
        DATE_YEAR: 'YYYY',
        DATE_YEAR_QUARTER: 'YYYY [Q]Q',
        DATE: 'DD-MMM-YYYY',
        DDMMMYY: 'DD-MMM-YY',
        DDMMM: 'DD-MMM',
        MMMDD: 'MMM DD',
        DAY_OF_WEEK: 'dddd',
        MMDDYYYY: 'MM-DD-YYYY',
        DDMMYYYY: 'DD-MM-YYYY',
        DDMMMYYYY: 'DD-MMM-YYYY',
        YYYYMMDD: 'YYYY-MM-DD',
        DDMMMMYY: 'DD-MMMM-YY',
        LOG_BACK_IN: 'MM/DD/YYYY hh:mm:ss A',
        DATE_TIME: 'DD-MMM-YYYY [at] hh:mm A',
        DATE_FULL_TIME: 'YYYY_DD_MM_H_mm_ss',
        HOURS: 'h:mm a',
        FULL_HOURS: 'HH:mm',
    }),
    'MM/DD/YYYY', 'DD/MM/YYYY'];

const isValidDate = (date: string, format: string): boolean => {
    const parsedDate = moment(date, format, true);
    return parsedDate.isValid() && parsedDate.format(format) === date;
};

const parseDateWithFormats = (dateStr: string): ReturnType<typeof moment> | null => {
    return AcceptableDateFormats
        .map(format => moment(dateStr.trim(), format, true))
        .find(parsedDate => parsedDate.isValid()) || null;
};

export const formatDateRange = (
    dateValue: string,
    dateFormat: string | undefined,
    validateDateCallback?: (date: ReturnType<typeof moment>) => {errorType?: ErrorType},
): {value: string | null, errorType?: ErrorType} => {
    if (!dateValue.trim() || !dateValue.includes(' to ') || !dateFormat) {
        return {value: null};
    }

    const [startStr, endStr] = dateValue.split(' to ').map(str => str.trim());

    if (!startStr || !endStr) {
        return {value: null};
    }

    const startDate = parseDateWithFormats(startStr);
    const endDate = parseDateWithFormats(endStr);

    if (!startDate || !endDate) {
        return {value: null, errorType: ERROR_TYPES.INVALID_DATE_RANGE};
    }

    if (startDate.isAfter(endDate)) {
        return {value: dateValue, errorType: ERROR_TYPES.INVALID_DATE_RANGE};
    }

    const formattedStartDate = formatDate(startDate, dateFormat);
    const formattedEndDate = formatDate(endDate, dateFormat);

    if (
        !isValidDate(formattedStartDate, dateFormat) ||
        !isValidDate(formattedEndDate, dateFormat)
    ) {
        return {value: dateValue, errorType: ERROR_TYPES.INVALID_DATE_RANGE};
    }

    if (validateDateCallback) {
        for (const date of [formattedStartDate, formattedEndDate]) {
            const validation = validateDateCallback(moment(date, dateFormat));
            if (validation?.errorType) {
                return {value: dateValue, errorType: validation.errorType};
            }
        }
    }

    return {value: `${formattedStartDate} to ${formattedEndDate}`};
};
